import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { downloadPdf } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Lock, Unlock, ChevronRight, AlertTriangle, Download } from "lucide-react";
import ProofCard from "@/components/ProofCard";
import { yearLabel } from "@/components/shared";
import { btnGhost } from "./adminShared";

const STATUS_META = {
  not_started: { label: "Pas commencé", cls: "bg-slate-50 text-slate-400" },
  draft: { label: "Brouillon", cls: "bg-sky-100 text-sky-600" },
  draft_complete: { label: "Prêt à soumettre", cls: "bg-amber-100 text-amber-600" },
  submitted: { label: "Soumis", cls: "bg-emerald-100 text-emerald-600" },
};

export default function FinalGradesTab() {
  const [students, setStudents] = useState(null);
  const [detailFor, setDetailFor] = useState(null);
  const [detail, setDetail] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [gapOnly, setGapOnly] = useState(false);

  const load = () => api.get("/admin/cap-dossier").then(({ data }) => setStudents(data));

  useEffect(() => { load(); }, []);

  const openDetail = (s) => {
    setDetailFor(s);
    setDetail(null);
    api.get(`/admin/cap-dossier/${s.id}`).then(({ data }) => setDetail(data));
  };

  const unlock = async () => {
    setUnlocking(true);
    try {
      await api.patch(`/admin/cap-dossier/${detailFor.id}/unlock`);
      toast.success("Dossier déverrouillé, l'élève peut le modifier ✓");
      openDetail(detailFor);
      load();
    } catch {
      toast.error("Échec du déverrouillage.");
    } finally {
      setUnlocking(false);
    }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      await downloadPdf(`/admin/cap-dossier/${detailFor.id}/pdf`, `dossier_final_${detailFor.name.replace(/\s+/g, "_")}.pdf`);
    } catch {
      toast.error("Échec de l'export PDF.");
    } finally {
      setExporting(false);
    }
  };

  const gapCount = students ? students.filter((s) => s.has_gap).length : 0;
  const visibleStudents = students ? (gapOnly ? students.filter((s) => s.has_gap) : students) : null;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-slate-500 font-medium">
          Dossiers finaux EP2/EP3 choisis par chaque élève (4 compétences par bloc), pour préparer les notes finales.
        </p>
        {students && gapCount > 0 && (
          <button
            onClick={() => setGapOnly((v) => !v)}
            data-testid="final-grades-gap-count"
            aria-pressed={gapOnly}
            title={gapOnly ? "Afficher tous les élèves" : "N'afficher que les élèves en lacune"}
            className={`inline-flex items-center gap-1.5 text-xs font-extrabold px-3 py-1.5 rounded-full shrink-0 transition-colors ${
              gapOnly ? "bg-rose-600 text-white" : "bg-rose-100 text-rose-600 hover:bg-rose-200"
            }`}
          >
            <AlertTriangle className="h-3.5 w-3.5" /> {gapCount} élève{gapCount > 1 ? "s" : ""} en lacune sur {students.length}
          </button>
        )}
      </div>

      {!students ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 text-orange-400 animate-spin" /></div>
      ) : (
        <div className="rounded-3xl bg-white border border-slate-100 shadow-sm divide-y divide-slate-100" data-testid="final-grades-list">
          {visibleStudents.length === 0 && (
            <p className="px-5 py-8 text-center text-sm text-slate-400 font-medium">Aucun élève en lacune 🎉</p>
          )}
          {visibleStudents.map((s) => {
            const st = STATUS_META[s.status];
            return (
              <button
                key={s.id}
                onClick={() => openDetail(s)}
                data-testid={`final-grades-row-${s.id}`}
                className="w-full flex items-center gap-4 px-5 py-3.5 hover:bg-slate-50 transition-colors text-left"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-800 truncate">{s.name}</p>
                  <p className="text-xs text-slate-400 font-bold">{yearLabel(s.year)}</p>
                </div>
                {s.has_gap && (
                  <span
                    title="Une compétence choisie n'a aucune preuve validée — dossier à risque d'être incomplet"
                    data-testid={`final-grades-gap-${s.id}`}
                    className="inline-flex items-center gap-1 text-[11px] font-extrabold px-2 py-1 rounded-full bg-rose-100 text-rose-600"
                  >
                    <AlertTriangle className="h-3 w-3" /> Lacune
                  </span>
                )}
                <span className={`text-xs font-extrabold px-2.5 py-1 rounded-full ${st.cls}`}>{st.label}</span>
                <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      <Dialog open={!!detailFor} onOpenChange={(o) => { if (!o) { setDetailFor(null); setDetail(null); } }}>
        <DialogContent className="rounded-3xl max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="final-grades-detail-modal">
          <DialogHeader>
            <DialogTitle className="font-display">Dossier final · {detailFor?.name}</DialogTitle>
            <DialogDescription>Compétences EP2 (Bloc 2) et EP3 (Bloc 3) choisies par l'élève, regroupées avec leurs preuves validées.</DialogDescription>
          </DialogHeader>

          {!detail ? (
            <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 text-orange-400 animate-spin" /></div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-between gap-3 rounded-2xl bg-slate-50 px-4 py-3">
                <div className="flex items-center gap-2 text-sm font-bold text-slate-600">
                  {detail.locked ? <Lock className="h-4 w-4 text-slate-500" /> : <Unlock className="h-4 w-4 text-slate-400" />}
                  {detail.locked
                    ? `Soumis le ${detail.submitted_at?.slice(0, 10).split("-").reverse().join("/")}`
                    : "Non soumis par l'élève"}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={exportPdf}
                    disabled={exporting}
                    data-testid="final-grades-export-pdf-btn"
                    className={btnGhost + " px-4 py-2 rounded-full border border-slate-200 text-xs disabled:opacity-50"}
                  >
                    {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Télécharger en PDF
                  </button>
                  {detail.locked && (
                    <button
                      onClick={unlock}
                      disabled={unlocking}
                      data-testid="final-grades-unlock-btn"
                      className={btnGhost + " px-4 py-2 rounded-full border border-slate-200 text-xs disabled:opacity-50"}
                    >
                      {unlocking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Unlock className="h-3.5 w-3.5" />} Déverrouiller pour correction
                    </button>
                  )}
                </div>
              </div>

              {[{ key: "ep2", label: "EP2 · Bloc 2" }, { key: "ep3", label: "EP3 · Bloc 3" }].map(({ key, label }) => (
                <div key={key}>
                  <h3 className="font-display text-lg font-semibold text-slate-900 mb-3">{label}</h3>
                  {detail[key].length === 0 ? (
                    <div className="rounded-2xl bg-slate-50 border border-dashed border-slate-200 p-5 text-center text-sm text-slate-400 font-medium">
                      L'élève n'a pas encore choisi ses compétences pour ce bloc.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {detail[key].map((group) => (
                        <div key={group.code} data-testid={`final-grades-comp-${group.code}`}>
                          <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400 mb-2">
                            {group.code} · {group.short}
                          </p>
                          {group.proofs.length === 0 ? (
                            <div className="rounded-2xl bg-rose-50 border border-dashed border-rose-200 p-4 flex items-center gap-2 text-sm text-rose-500 font-bold" data-testid={`final-grades-comp-gap-${group.code}`}>
                              <AlertTriangle className="h-4 w-4" /> Aucune preuve validée pour cette compétence.
                            </div>
                          ) : (
                            <div className="grid sm:grid-cols-2 gap-3">
                              {group.proofs.map((p) => <ProofCard key={p.id} proof={p} reviewMode onChanged={() => openDetail(detailFor)} />)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
